import React, { useEffect, useMemo } from "react";
import { Route, Redirect, Switch } from "react-router";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import {
  App,
  RoomSelection,
  HealthCheck,
  Checkout,
  BookingConfirmation,
  BookingModification,
  MyAccount,
  MyReservations,
} from "~/containers";

import {
  PayNoPainBridge,
  PayNoPainV2BridgeWrapper,
  WitbookingPaymentBridge,
  RecoverBooking,
  RecoverReservationConfirmation,
} from "~/components";
import constants from "./utils/constants";
import BookingConfirmationSmartPayment from "./containers/BookingConfirmationSmartPayment/BookingConfirmationSmartPayment";
import { smartPaymentModeSelector } from "./containers/BookingConfirmationSmartPayment/selectors";
import { reservationSelector } from "./containers/App/selectors";
import RESERVATION_STATUS_EXTENDED from "./utils/constants";
import Calendar from "./components/Calendar/Calendar";
import { getLinkToLoyaltyLogin } from "./utils/misc";

const appSelectors = require("./containers/App/selectors");

const AppRoutedComponent = ({ match, ...props }) => {
  let baseUrl = match.url.endsWith("/") ? match.url.slice(0, -1) : match.url;
  const url = match.url.slice(0, baseUrl.lastIndexOf("/"));
  const smartPaymentMode = useSelector(smartPaymentModeSelector);
  const reservation = useSelector(reservationSelector);
  const deferredConfirmed =
    reservation &&
    reservation.get("paymentStatus") === constants.RESERVATION_STATUS_EXTENDED.UNCONFIRMED;
  const pathname = props?.location?.pathname;
  const firstSlashIndex = pathname?.indexOf("/");
  const lastSlashIndex = pathname?.lastIndexOf("/");
  const propertyTicker = pathname?.substring(
    firstSlashIndex + 1,
    pathname?.indexOf("/", firstSlashIndex + 1)
  );
  const inventoryTicker = pathname?.substring(lastSlashIndex + 1, pathname.length);

  return (
    <App match={match} {...props}>
      {appProps => (
        <Switch>
          <Route path={`${url}/widget/calendar/:id`}>
            <Calendar inventoryTicker={inventoryTicker} propertyTicker={propertyTicker} iframe />
          </Route>
          <Route path={`${url}/${constants.STEP_PATHS.STEP_1}`}>
            <RoomSelection {...appProps} />
          </Route>
          <Route path={`${url}/${constants.STEP_PATHS.STEP_2}`}>
            <Checkout {...appProps} />
          </Route>

          <Route path={`${url}/${constants.STEP_PATHS.STEP_3}/:reservationId`}>
            {childProps => {
              if (smartPaymentMode && deferredConfirmed) {
                return (
                  <BookingConfirmationSmartPayment
                    {...appProps}
                    hotelTicker={match.params.hotelTicker}
                    reservationId={childProps.match.params.reservationId}
                    history={childProps.history}
                  />
                );
              } else {
                return (
                  <BookingConfirmation
                    {...appProps}
                    hotelTicker={match.params.hotelTicker}
                    reservationId={childProps.match.params.reservationId}
                  />
                );
              }
            }}
          </Route>

          <Route path={`${url}/${constants.STEP_PATHS.STEP_4}/:reservationId`}>
            {childProps => (
              <BookingModification
                {...appProps}
                hotelTicker={match.params.hotelTicker}
                reservationId={childProps.match.params.reservationId}
              />
            )}
          </Route>
        </Switch>
      )}
    </App>
  );
};

export default () => {
  /**
   * Please keep routes in alphabetical order
   */
  const loyaltyMyAccount = {
    showBackArrow: true,
    myAccountClicked: true,
  };

  const loyaltyMyReservations = {
    showBackArrow: true,
    myReservationsClicked: true,
  };

  return (
    <Switch>
      <Route path={`/${constants.STEP_PATHS.HEALTH_CHECK}`} component={HealthCheck} />
      <Route
        path={`/${constants.STEP_PATHS.SHARE_BOOKING}/:hotelTicker/:locale?/:key`}
        component={RecoverBooking}
      />
      <Route
        path={`/${constants.STEP_PATHS.RECOVER_RESERVATION_CONFIRMATION}/:locale?/:reservationId`}
        component={RecoverReservationConfirmation}
      />
      <Route
        path="/:hotelTicker/payments/paynopain/:reservationId/:status?"
        component={PayNoPainBridge}
      />
      <Route
        path="/:hotelTicker/payments/paynopainv2/:reservationId/:status?"
        component={PayNoPainV2BridgeWrapper}
      />
      <Route
        path={`/:hotelTicker/payments/${constants.PAYMENT_TYPES.WITBOOKING}/:locale?/:reservationId/:status?`}
        component={WitbookingPaymentBridge}
      />
      <Route path={`/:hotelTicker/loyalty/my-account`}>
        {props => (
          <App {...loyaltyMyAccount} {...props}>
            {appProps => <MyAccount {...appProps} />}
          </App>
        )}
      </Route>
      <Route path={`/:hotelTicker/loyalty/my-reservations`}>
        {props => (
          <App {...loyaltyMyReservations} {...props}>
            {appProps => <MyReservations {...appProps} />}
          </App>
        )}
      </Route>
      <Route>
        <Switch>
          <Route
            path={`/:hotelTicker/:locale?/:step(${constants.STEP_PATHS.STEP_1}|${constants.STEP_PATHS.STEP_2}|${constants.STEP_PATHS.STEP_3}|${constants.STEP_PATHS.STEP_4}|${constants.STEP_PATHS.WIDGET})`}
            component={AppRoutedComponent}
          />
          <Route exact path={`/:hotelTicker/:locale?`}>
            {({ match }) => (
              <Redirect
                to={{
                  pathname: `${match.url.replace(/\/$/, "")}/${constants.STEP_PATHS.STEP_1}`,
                  search: window.location.search,
                }}
              />
            )}
          </Route>
          <Route path={`/:hotelTicker/*`}>{props => <App status={404} {...props} />}</Route>
        </Switch>
      </Route>
    </Switch>
  );
};
